import { uniqBy } from 'lodash-es';

import {
  type AuthForgotPasswordSchema,
  type AuthHttpPostAuthMultidomainLoginHeader,
  type AuthMultiDomainLoginSchema,
  type AuthMultiPlatformDomainUserSystemSchema,
  type AuthSimpleLoginSchema,
  httpDeleteAuthToken,
  httpPostAuthMultidomainLogin,
  httpPostAuthOtpGenerate,
  httpPostAuthSimpleLogin,
  httpPostPasswordForgot,
} from '@iconik-io/iconik-api/src/services/auth';
import { httpGetUsersCurrent } from '@iconik-io/iconik-api/src/services/users';
import { destroySentryUserContext } from '@iconik-io/shared-lib/src/error_tracking/sentry/destroy_sentry_user_context';
import { initSentryUserContext } from '@iconik-io/shared-lib/src/error_tracking/sentry/init_sentry_user_context';
import { setUserRoles } from '@iconik-io/shared-lib/src/redux/auth/slice';
import { rolesArrayToDictionary } from '@iconik-io/shared-lib/src/redux/auth/utils';
import {
  clearStorageCredentials,
  setStorageCredentials,
} from '@iconik-io/shared-lib/src/redux/middlewares/storage/credentials';
import {
  handleErrors,
  isUnauthorizedError,
} from '@iconik-io/shared-lib/src/utils/handle_messages';

import type { SystemDomainStatus } from '../../../constants/system_domains';
import type { AppDispatch, GetState } from '../../../store/types';
import { getSafeNextUrl } from '../../../utils/get_safe_next_url';
import { destroySamlSession } from '../../../utils/saml';
import { setSystemDomainStatus } from '../../app/slice';
import { initApp } from '../../app/thunks';
import {
  historyPush,
  historyReplace,
  windowLocationChange,
} from '../../history/actions';
import { skipRedirectOnLogin } from '../../http/actions';
import { getRoles } from '../../roles/thunks';
import {
  clearResetPasswordData,
  endLogin,
  forgotPassword as forgotPasswordAction,
  loginError,
  logout as logoutAction,
  setAuthDomainList,
  setCurrentUser,
  setCurrentUserId,
  startLogin,
} from '../slice';
import type { AuthUserData } from '../types';

export function logout(redirectPath = '/login') {
  return (dispatch: AppDispatch): void => {
    dispatch(httpDeleteAuthToken())
      // An already invalid token answers 401, and we log out either way.
      .catch((error: unknown) => {
        if (!isUnauthorizedError(error)) {
          throw error;
        }
      })
      .finally(() => {
        destroySentryUserContext();
        dispatch(logoutAction());
        // Each host decides how to get there: the web app assigns
        // window.location, while the memory-router hosts navigate in place —
        // reloading them would restart the app at '/' and bounce straight back
        // here on the next unauthorized request.
        dispatch(windowLocationChange(redirectPath));
      });
  };
}

export function getCurrentUser() {
  return (dispatch: AppDispatch) =>
    dispatch(initApp()).then(({ roles, user }) => {
      if (user && user.type === 'BROWSE_API_ONLY') {
        dispatch(logout());
      }
      if (Object.keys(user).length === 0) {
        throw new Error('Cannot load user data');
      }
      initSentryUserContext(user);
      const userData = {
        ...user,
        roles,
      };

      dispatch(setCurrentUser(userData));

      dispatch(setCurrentUserId(user.id));
      dispatch(setUserRoles(rolesArrayToDictionary(roles)));
    });
}

type InitiateLoginParams = {
  user_id?: string;
  app_id?: string;
  system_domain_status?: SystemDomainStatus;
  token?: string | null;
  skip_redirect?: boolean;
};

export function initiateLogin({
  user_id,
  app_id,
  token,
  system_domain_status,
  skip_redirect = false,
}: InitiateLoginParams) {
  return (dispatch: AppDispatch, getState: GetState) => {
    dispatch(setCurrentUserId(user_id));
    if (app_id && token) {
      setStorageCredentials(app_id, token);
    }
    dispatch(setSystemDomainStatus(system_domain_status));

    if (!skip_redirect) {
      const query = getState().routeParams.query;
      // query.next is already percent-decoded by the router's qs.parse.
      // Decoding again would strip the encoding of the target URL's own
      // query params (e.g. an OAuth redirect_uri). It is not necessarily a
      // string either: qs parses a repeated ?next= into an array.
      const url = getSafeNextUrl(query.next);
      if (url !== null) {
        // For new review experience we need to trigger location change
        // with reloading of the page. Otherwise navigation will be handled
        // in the scope of iconik app
        if (url.startsWith('/review')) {
          const currentUrl = new URL(window.location.href);
          dispatch(
            windowLocationChange(
              `${currentUrl.protocol}//${currentUrl.host}${url}`,
            ),
          );
        } else {
          dispatch(historyReplace(url));
        }
      } else {
        dispatch(historyReplace('/'));
      }
    }

    return Promise.all([dispatch(getCurrentUser()), dispatch(getRoles())]);
  };
}

export function simpleLogin(data: AuthSimpleLoginSchema) {
  return (
    dispatch: AppDispatch,
    getState: GetState,
  ): Promise<null | AuthMultiPlatformDomainUserSystemSchema[]> => {
    dispatch(loginError([]));
    dispatch(startLogin('simple'));

    return dispatch(httpPostAuthSimpleLogin(data)).then((response) => {
      const { auth_system_domains } = response.body;
      if (auth_system_domains) {
        const domainList = getState().authReducer.authDomainList;
        dispatch(
          setAuthDomainList(
            uniqBy([...auth_system_domains, ...domainList], 'system_domain_id'),
          ),
        );
        return auth_system_domains;
      }
      dispatch(initiateLogin(response.body)).then(() => {
        dispatch(endLogin('simple'));
      });
      return null;
    });
  };
}

export function simpleLoginForExtension(data: AuthSimpleLoginSchema) {
  return (dispatch: AppDispatch, getState: GetState): Promise<unknown> => {
    dispatch(loginError([]));
    dispatch(startLogin('simple'));

    return dispatch(httpPostAuthSimpleLogin(data))
      .then((response) => {
        const { auth_system_domains } = response.body;
        if (auth_system_domains) {
          const domainListUrl = `/login/domain${window.location.search}`;
          const domainList = getState().authReducer.authDomainList;
          dispatch(
            setAuthDomainList(
              uniqBy(
                [...auth_system_domains, ...domainList],
                'system_domain_id',
              ),
            ),
          );
          dispatch(historyPush(domainListUrl));
          return null;
        }
        dispatch(initiateLogin(response.body)).then(() => {
          dispatch(httpGetUsersCurrent()).then(({ body }) => {
            analytics.identify(body.id);
          });
          dispatch(endLogin('simple'));
        });
        return null;
      })
      .catch((error) => {
        let errors = handleErrors(error);
        if (errors.length === 0 && error.cause.code === 'ERR_NETWORK') {
          errors = [
            'Login failed. Please check your configured host and network connection.',
          ];
          window.localStorage.removeItem('host');
        }
        dispatch(loginError(errors));
      });
  };
}

export function multiDomainLogin(
  data: AuthMultiDomainLoginSchema,
  headers: AuthHttpPostAuthMultidomainLoginHeader,
) {
  return (dispatch: AppDispatch): Promise<unknown> => {
    dispatch(startLogin('simple'));

    return dispatch(httpPostAuthMultidomainLogin(data, {}, headers))
      .then((response) => dispatch(initiateLogin(response.body)))
      .then(() => dispatch(endLogin('simple')));
  };
}

export function multiDomainLoginForExtension(
  data: AuthMultiDomainLoginSchema,
  headers: AuthHttpPostAuthMultidomainLoginHeader,
) {
  return (dispatch: AppDispatch): Promise<unknown> => {
    return dispatch(multiDomainLogin(data, headers))
      .then(() =>
        dispatch(httpGetUsersCurrent()).then(({ body }) => {
          analytics.identify(body.id);
        }),
      )
      .catch((error) => {
        dispatch(loginError(handleErrors(error)));
      });
  };
}

export function resetLoginData() {
  return (dispatch: AppDispatch) => {
    destroySamlSession();
    clearStorageCredentials();
    dispatch(setCurrentUserId(undefined));
  };
}

export function forgotPassword(data: AuthForgotPasswordSchema) {
  return (dispatch: AppDispatch): Promise<unknown> => {
    dispatch(clearResetPasswordData());
    return dispatch(httpPostPasswordForgot(data)).then(() =>
      dispatch(forgotPasswordAction()),
    );
  };
}

export function updateCurrentUser(data: Partial<AuthUserData>) {
  return (dispatch: AppDispatch, getState: GetState): void => {
    const userData: AuthUserData = {
      ...getState().authReducer.userData,
      ...data,
    };
    dispatch(setCurrentUser(userData));
  };
}

export function requestOtpCodeViaEmail(
  tempToken: string,
  domainId: string,
  email: string,
) {
  return (dispatch: AppDispatch) => {
    return dispatch(
      httpPostAuthOtpGenerate(
        { email, system_domain_id: domainId },
        { 'Temp-Auth-Token': tempToken },
      ),
    );
  };
}

export const getCurrentUserInShare = () => (dispatch: AppDispatch) =>
  dispatch(skipRedirectOnLogin(httpGetUsersCurrent()));
